import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../../shared/api'

export type Rolle = 'Projektleiter' | 'Standortleiter' | 'Ersatz'

export interface PersonRef {
  id: number          // SchuleStaff row id
  mainId: number      // fm.Personen.MainID
  name: string
  email: string | null
  handy: string | null
}

export interface MitarbeiterRef {
  id: number
  mainId: number
  name: string
  email: string | null
  handy: string | null
  crossFirma: boolean
  ferienplan: boolean
}

export interface HierarchieNode {
  id: string
  name: string
  stadt: string | null
  plz: string | null
  adresse: string | null
  status: string | null
  gruppeAbk: string | null
  loginName: string | null
  kategorie: string | null
  istHauptschule: boolean
  standortleiter: PersonRef | null
  projektleiter: PersonRef | null
  ersatz: PersonRef[]
  mitarbeiter?: MitarbeiterRef[]
}

export interface HierarchieGruppe {
  abkuerzung: string | null
  hauptschule: HierarchieNode
  standorte: HierarchieNode[]
  staffCount: number
  mitarbeiter?: MitarbeiterRef[]
}

export function useHierarchie() {
  return useQuery({
    queryKey: ['hierarchie'],
    queryFn: () => api.get<HierarchieGruppe[]>('/api/hierarchie'),
    staleTime: 60_000,
  })
}

export interface AssignStaffInput {
  schuleId: string
  personMainId: number
  rolle: Rolle
  reihenfolge?: number
}

/**
 * Hierarchie writes go to ops.Einsatz (the new canonical store).
 * /api/hierarchie reads from ops.Einsatz too, so writes are immediately
 * visible in the UI. Previously this hit /api/schule-staff which writes
 * to ops.SchuleStaff — invisible to the Einsatz-derived view.
 */
export function useAssignStaff() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AssignStaffInput) =>
      api.post<{ id: number }>('/api/einsatz', {
        personId: input.personMainId,
        schuleId: input.schuleId,
        rolle: input.rolle,
        von: new Date().toISOString().slice(0, 10),
        reihenfolge: input.reihenfolge,
      }).then(({ id }) => ({ id, mainId: input.personMainId, name: '', email: null, handy: null } as PersonRef)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hierarchie'] }),
  })
}

/**
 * "Unassign" closes the deployment by setting bis=today (PUT).
 * NOT a hard DELETE — historical record is preserved.
 */
export function useUnassignStaff() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (einsatzId: number) =>
      api.put(`/api/einsatz/${einsatzId}`, {
        bis: new Date().toISOString().slice(0, 10),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hierarchie'] }),
  })
}

export function useReorderStaff() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reihenfolge }: { id: number; reihenfolge: number }) =>
      api.put(`/api/einsatz/${id}`, { reihenfolge }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hierarchie'] }),
  })
}
